import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Compass, Phone, ShieldCheck, HandCoins, HeartHandshake, Users } from "lucide-react";
import useSeo from "../hooks/useSeo";
import { company } from "../mock";

const PATHWAYS = [
  {
    icon: ShieldCheck, title: "Ohio DODD Waivers", to: "/dodd",
    text: "For individuals with developmental disabilities. Services like HPC, respite and transportation are authorized through your County Board of Developmental Disabilities and SSA. Start by contacting your county board — then send us a referral.",
  },
  {
    icon: HeartHandshake, title: "Next Generation MyCare Ohio", to: "/mycare",
    text: "For Ohioans with both Medicare and Medicaid. Managed-care plans coordinate benefits that can include in-home personal care and homemaker support. We verify your plan's eligibility and authorization before care begins.",
  },
  {
    icon: Users, title: "PASSPORT Waiver", to: "/mycare",
    text: "Ohio's Medicaid waiver for adults 60+ who need help at home, administered through the Ohio Department of Aging and your regional Area Agency on Aging. Contact your AAA for an assessment — we can help you understand the steps.",
  },
  {
    icon: HandCoins, title: "Private Pay", to: "/private-pay",
    text: "No waiver, no waiting on program enrollment. Flexible personal care, homemaker support, companionship, respite and transportation support where staffing allows — often the fastest way to start care.",
  },
];

const STEPS = [
  { n: "1", t: "Tell us what's happening", d: "Call (614) 526-1548 or send a request online. Share who needs care, where they live and what kind of help they need." },
  { n: "2", t: "We identify the right pathway", d: "DODD, MyCare Ohio, PASSPORT or private pay — we help you understand which route fits and what it covers." },
  { n: "3", t: "Eligibility is verified", d: "For program-funded care we confirm eligibility, authorization and participation with your plan or county board before services begin." },
  { n: "4", t: "Care begins", d: "We match a qualified, background-checked caregiver and stay in close communication with your family and care team." },
];

const FAQS = [
  { q: "How much does home care cost in Ohio?", a: "It depends on the pathway. Waiver programs (DODD, PASSPORT, MyCare Ohio) can cover authorized services with little or no cost to the family. Private-pay rates vary by schedule and services — contact us for current rates and availability." },
  { q: "How fast can home care start?", a: "Private-pay care can often begin quickly, subject to staffing. Waiver-funded care starts after eligibility and authorization are confirmed with your plan or county board. Either way, the first step is a phone call — (614) 526-1548." },
  { q: "Can a family member get paid to be the caregiver?", a: "Under some Ohio participant-directed programs, a qualified family member or friend may provide approved services. Eligibility depends on the waiver, service plan and program rules — see our family caregiver pathway." },
  { q: "Do you serve my county?", a: "We are headquartered in Columbus and accept referral inquiries from all 88 Ohio counties. Actual availability depends on staffing, location and schedule — call us to check your area." },
];

export default function HomeCareGuide() {
  useSeo({
    title: "How to Get Home Care in Ohio (2026 Guide) | DODD, MyCare, PASSPORT & Private Pay",
    description: "A plain-English guide to getting home care in Ohio: DODD waivers, Next Generation MyCare, PASSPORT and private pay — who qualifies, how to start, and what it costs. Call (614) 526-1548.",
    keywords: "how to get home care in Ohio, who pays for home care Ohio, DODD waiver home care, PASSPORT waiver how to apply, MyCare Ohio home care, private pay home care cost Ohio",
    path: "/how-to-get-home-care-in-ohio",
  });
  return (
    <>
      <section className="brand-gradient text-white">
        <div className="mx-auto max-w-7xl px-4 py-14">
          <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
            <Compass className="h-4 w-4" /> Family guide
          </div>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl max-w-3xl">How to Get Home Care in Ohio</h1>
          <p className="mt-4 text-white/90 max-w-2xl">
            Four pathways pay for home care in Ohio. Here's how each works, who qualifies, and how to start —
            without the runaround.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/contact"><Button data-testid="guide-request-btn" className="rounded-full bg-white text-[#0E5A72] hover:bg-white/90 h-12 px-7 font-semibold">REQUEST HOME CARE</Button></Link>
            <a href={`tel:${company.phoneRaw}`}><Button className="rounded-full bg-[#2E9E6A] hover:bg-[#23875A] text-white h-12 px-7 font-semibold"><Phone className="mr-2 h-4 w-4" /> {company.phone}</Button></a>
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="font-display font-bold text-3xl text-[#0E2A33] text-center">The four pathways</h2>
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            {PATHWAYS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="bg-[#F7FBFC] rounded-2xl border border-slate-100 p-7">
                  <div className="brand-gradient h-11 w-11 rounded-xl grid place-items-center text-white mb-4"><Icon className="h-5 w-5" /></div>
                  <h3 className="font-display font-bold text-lg text-[#0E2A33]">{p.title}</h3>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{p.text}</p>
                  <Link to={p.to} className="text-[#0E5A72] text-sm font-medium hover:underline mt-3 inline-block">Learn more →</Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section bg-[#F7FBFC]">
        <div className="mx-auto max-w-5xl px-4">
          <h2 className="font-display font-bold text-3xl text-[#0E2A33] text-center">Getting started in 4 steps</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s) => (
              <div key={s.n} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                <div className="font-display font-extrabold text-3xl text-[#2E9E6A]">{s.n}</div>
                <div className="font-semibold text-[#0E2A33] mt-2">{s.t}</div>
                <p className="text-xs text-slate-500 mt-2 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="mx-auto max-w-4xl px-4">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-[#0E2A33] text-center">Common questions from Ohio families</h2>
          <div className="mt-8 space-y-4">
            {FAQS.map((f) => (
              <div key={f.q} className="bg-[#F7FBFC] rounded-2xl border border-slate-100 p-6">
                <div className="font-semibold text-[#0E2A33]">{f.q}</div>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8 text-sm text-slate-600">
            Related: <Link to="/hpc" className="text-[#0E5A72] font-medium hover:underline">HPC Services</Link> ·{" "}
            <Link to="/respite-care" className="text-[#0E5A72] font-medium hover:underline">Respite Care</Link> ·{" "}
            <Link to="/family-caregiver" className="text-[#0E5A72] font-medium hover:underline">Family Caregiver Options</Link>
          </div>
        </div>
      </section>

      <section className="section brand-gradient text-white">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <h2 className="font-display font-bold text-3xl">Now accepting new clients</h2>
          <p className="mt-3 text-white/90">One call and we'll help you figure out the right pathway — {company.phone}.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/contact"><Button className="rounded-full bg-white text-[#0E5A72] hover:bg-white/90 h-12 px-8 font-semibold">Request Home Care</Button></Link>
            <Link to="/refer"><Button className="rounded-full bg-[#2E9E6A] hover:bg-[#23875A] text-white h-12 px-8 font-semibold">Refer a Client</Button></Link>
          </div>
        </div>
      </section>
    </>
  );
}
